import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { Helmet } from 'react-helmet'
import { RouteComponentProps } from 'react-router'
import { useHistory } from 'react-router-dom'

import { PanelDetails } from 'components/attacks/details/AttackStyles'
import Content from 'components/common/Content'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'components/common/Tabs'
import Text from 'components/common/Text'
import Header, { Heading } from 'components/navigation/Header'
import IconCounterGroup from 'components/rbac-settings/IconCounterGroup'
import { withAuthContext } from 'context/with-auth-context'
import useAgent from 'hooks/use-agent'
import theme from 'theme'
import { ApiKey } from 'types/api/api-key'
import { getUserDisplayName, UserIdentity } from 'utils/user'

import ApiKeysTable from './ApiKeysTable'
import Mfa from './MFA'
import ResetPassword from './ResetPassword'
import UserDetails from './UserDetails'
import UserIntegrations from './UserIntegrations'

// memoized so it does not re-render unnecessarily
// Profile re-renders on a regular basis because of AuthenticatedRoutes' polled profile called
const ProfileContents: React.FC<{
  session: UserIdentity & {
    teams: Record<string, any> // TODO does UserIdentity need to be updated? this maybe should come from api.d.ts
  }
  tab: string
}> = React.memo(({ session, tab }) => {
  const history = useHistory()
  const teams = session?.teams ?? []
  const {
    userLevelApiKeys: isRbacApiEnabled,
    jiraIntegration: isJiraIntegrationEnabled
  } = useFlags()

  const {
    data: apiKeys,
    isLoading: isLoadingApiKeys,
    hasError: hasErrorApiKeys,
    doFetch
  }: {
    data: ApiKey[]
    isLoading: boolean
    hasError: boolean
    doFetch: () => void
  } = useAgent('/apikeys', [], { skipRequest: !isRbacApiEnabled })

  const tabs = [
    {
      name: 'details',
      tabTitle: 'Details',
      tabPanel: <UserDetails user={session} />,
      isEnabled: true
    },
    {
      name: 'security',
      tabTitle: 'Security',
      tabPanel: (
        <PanelDetails>
          <ResetPassword />
          <Mfa session={session} />
        </PanelDetails>
      ),
      isEnabled: true
    },
    {
      name: 'apikeys',
      tabTitle: 'API Keys',
      tabPanel: (
        <ApiKeysTable
          apiKeysLimit={5}
          doRefresh={doFetch}
          hasError={hasErrorApiKeys}
          isLoading={isLoadingApiKeys}
          keys={apiKeys ?? []}
        />
      ),
      isEnabled: isRbacApiEnabled
    },
    {
      name: 'integrations',
      tabTitle: 'Integrations',
      tabPanel: <UserIntegrations />,
      isEnabled: isJiraIntegrationEnabled
    }
  ].filter((t) => t.isEnabled)
  const activeTabIndex = tabs.findIndex((t) => t.name === tab)

  return (
    <>
      {isRbacApiEnabled && (
        <>
          <Text as="h1">{getUserDisplayName(session)}</Text>
          <IconCounterGroup
            counts={[
              {
                icon: 'key',
                label: 'API Key',
                count: apiKeys?.length
              },
              {
                icon: 'users',
                label: 'Team',
                count: teams.length
              }
            ]}
            css={{
              marginBottom: theme.space[3]
            }}
          />
        </>
      )}

      <Tabs
        index={activeTabIndex > -1 ? activeTabIndex : 0}
        onChange={(route) => {
          return history.replace(`/profile/${tabs[route].name}`)
        }}
      >
        <TabList>
          {tabs.map((t) => (
            <Tab key={t.name} data-test-tab={t.name}>
              {t.tabTitle}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((t) => (
            <TabPanel key={t.name}>{t.tabPanel}</TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  )
})

type ProfileProps = {
  authState: Record<string, any>
}
type ProfileRouteProps = ProfileProps & RouteComponentProps<{ tab: string }>

const Profile: React.FC<ProfileRouteProps> = ({ authState, match }) => {
  return (
    <>
      <Helmet title="Account Settings" />
      <Header>
        <Heading>Account Settings</Heading>
      </Header>
      <Content id="Profile">
        <ProfileContents session={authState.session} tab={match.params.tab} />
      </Content>
    </>
  )
}

export default withAuthContext(Profile)
